// Storage layer for tokens + PKCE. This is where admin and liff diverge:
//   admin: localStorage for tokens + PKCE, keys "admin_tokens"/"admin_pkce"
//   liff:  sessionStorage for tokens, localStorage for PKCE, keys "booking_*"
// Both are produced from this factory by passing the storage kind + keys.
// All accessors are SSR-safe (return null/no-op when the storage is absent).

import type { TokenData } from "./oauth-core";

type StorageKind = "local" | "session";

function resolveStorage(kind: StorageKind): Storage | null {
  const store = kind === "local" ? globalThis.localStorage : globalThis.sessionStorage;
  return typeof store === "undefined" ? null : store;
}

export interface TokenStore {
  saveTokens(tokens: TokenData): void;
  getTokens(): TokenData | null;
  clearTokens(): void;
  getAccessToken(): string | null;
  savePKCE(data: { codeVerifier: string; state: string }): void;
  getPKCE(): { codeVerifier: string; state: string } | null;
  clearPKCE(): void;
}

export interface TokenStoreConfig {
  /** Backing store for the token bundle. admin="local", liff="session". */
  tokenStorage: StorageKind;
  /** Backing store for the PKCE challenge. Both apps use "local". */
  pkceStorage: StorageKind;
  tokenKey: string;
  pkceKey: string;
  /** PKCE entries older than this are treated as expired. */
  pkceTtlMs?: number;
}

export function createTokenStore(config: TokenStoreConfig): TokenStore {
  const { tokenStorage, pkceStorage, tokenKey, pkceKey, pkceTtlMs = 10 * 60 * 1000 } = config;

  function saveTokens(tokens: TokenData): void {
    resolveStorage(tokenStorage)?.setItem(tokenKey, JSON.stringify(tokens));
  }

  function getTokens(): TokenData | null {
    const raw = resolveStorage(tokenStorage)?.getItem(tokenKey);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function clearTokens(): void {
    resolveStorage(tokenStorage)?.removeItem(tokenKey);
  }

  function getAccessToken(): string | null {
    return getTokens()?.access_token ?? null;
  }

  function savePKCE(data: { codeVerifier: string; state: string }): void {
    resolveStorage(pkceStorage)?.setItem(pkceKey, JSON.stringify({ ...data, ts: Date.now() }));
  }

  function getPKCE(): { codeVerifier: string; state: string } | null {
    const raw = resolveStorage(pkceStorage)?.getItem(pkceKey);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (Date.now() - parsed.ts > pkceTtlMs) {
        clearPKCE();
        return null;
      }
      return { codeVerifier: parsed.codeVerifier, state: parsed.state };
    } catch {
      return null;
    }
  }

  function clearPKCE(): void {
    resolveStorage(pkceStorage)?.removeItem(pkceKey);
  }

  return { saveTokens, getTokens, clearTokens, getAccessToken, savePKCE, getPKCE, clearPKCE };
}
